/* A homework's repository, as the steps a student takes (D-051, D-052, D-053).
 *
 * Enrolled students do not make their repository: provision.mjs creates it,
 * private, in the student organisation, named <term>-<template>-<username>, once they
 * have given their username in Canvas and joined the organisation. So the
 * panel is:
 *
 *   1. open your Codespace — codespaces.new on THEIR repository, with
 *      quickstart, which resumes the Codespace they already have;
 *   2. stop it when done — hours are a monthly allowance.
 *
 * The site is for anyone, enrolled or not (D-001), so a reader who is not in
 * the course can still create a repository of their own from the template —
 * private, so nothing they write is public by accident. Until
 * `assignments.students_org` is set, that is the only route shown.
 *
 * The GitHub username is remembered in this browser only, as a convenience
 * (like the theme and the track). Nothing is sent anywhere: it only builds a
 * link. Signing in through the site would remove the typing; not this year.
 */
import { useState } from 'react';
import s from './RepoPanel.module.css';
import { studentRepo } from '../lib/studentRepo.js';

const KEY = 'wwcc.github-user';
const VALID = /^[A-Za-z0-9](?:[A-Za-z0-9]|-(?=[A-Za-z0-9])){0,38}$/;   // GitHub's username rule

function loadUser() {
  try { return localStorage.getItem(KEY) || ''; } catch { return ''; }
}


export default function RepoPanel({ org, name, studentsOrg }) {
  const [user, setUser] = useState(loadUser);
  const clean = user.trim().replace(/^@/, '');
  const ok = VALID.test(clean);
  const remember = (v) => { setUser(v); try { localStorage.setItem(KEY, v.trim().replace(/^@/, '')); } catch { /* session only */ } };

  const create = `https://github.com/new?${new URLSearchParams({
    template_owner: org, template_name: name, name, visibility: 'private',
  })}`;
  // enrolled: the repository provision.mjs made; otherwise the reader's own
  const repoPath = ok && (studentsOrg ? `${studentsOrg}/${studentRepo(name, clean)}` : `${clean}/${name}`);
  const mine = repoPath ? `https://github.com/${repoPath}` : null;
  const space = repoPath ? `https://codespaces.new/${repoPath}?quickstart=1` : null;
  let n = 0;

  return (
    <div>
      {studentsOrg && (
        <p className={s.lead}>
          <strong>Your repository is made for you</strong> — private, only you and your instructor can
          see it. It appears once you have given your GitHub username in Canvas and accepted the
          course's <a className={s.link} href={`https://github.com/orgs/${studentsOrg}/invitation`} target="_blank" rel="noreferrer">invitation to join {studentsOrg}</a>.
        </p>
      )}
      <ol className={s.panel}>
        {!studentsOrg && (
          <li className={s.row}>
            <span className={s.n}>{++n}</span>
            <div className={s.body}>
              <h3 className={s.title}>Create your repository</h3>
              <p className={s.text}>Once per homework. GitHub opens with the form filled in — keep the name as it is, and click <strong>Create repository</strong>.</p>
              <a className={s.primary} href={create} target="_blank" rel="noreferrer">Create my repository →</a>
            </div>
          </li>
        )}

        <li className={s.row}>
          <span className={s.n}>{++n}</span>
          <div className={s.body}>
            <h3 className={s.title}>Open your Codespace</h3>
            <p className={s.text}>Every time you work. It opens the Codespace you already have for your repository, or offers to create one.</p>
            <div className={s.userRow}>
              <label className={s.label} htmlFor={`gh-${name}`}>Your GitHub username</label>
              <input
                id={`gh-${name}`} className={s.input} value={user} spellCheck={false}
                autoComplete="username" autoCapitalize="none" placeholder="username"
                onChange={(e) => remember(e.target.value)}
                aria-invalid={clean !== '' && !ok}
              />
            </div>
            {clean !== '' && !ok && <p className={s.warn}>That isn't a GitHub username — letters, numbers and single hyphens only.</p>}
            <div className={s.actions}>
              {ok
                ? <a className={s.primary} href={space} target="_blank" rel="noreferrer">Open my Codespace →</a>
                : <span className={s.primaryOff} aria-disabled="true">Open my Codespace →</span>}
              {ok && <a className={s.link} href={mine} target="_blank" rel="noreferrer">My repository</a>}
            </div>
            <p className={s.aside}>On your own computer instead? Type your username, open <em>My repository</em>, then <strong>Code › Open with GitHub Desktop</strong> to clone it.</p>
          </div>
        </li>

        <li className={s.row}>
          <span className={s.n}>{++n}</span>
          <div className={s.body}>
            <h3 className={s.title}>Done for now? Stop it</h3>
            <p className={s.text}>
              A running Codespace uses your free monthly hours. Your files are kept when it stops, but they
              only reach GitHub — where your work is checked — when you <strong>commit and push</strong>.
              Then go to your Codespaces, click <strong>⋯</strong> beside this one, and choose <strong>Stop codespace</strong>.
            </p>
            <a className={s.secondary} href="https://github.com/codespaces" target="_blank" rel="noreferrer">My Codespaces →</a>
          </div>
        </li>
      </ol>
      {studentsOrg && (
        <p className={s.aside}>
          Not in the course? <a className={s.link} href={create} target="_blank" rel="noreferrer">Create a private repository of your own</a> from
          the <a className={s.link} href={`https://github.com/${org}/${name}`} target="_blank" rel="noreferrer">template</a>, then open it from your repository's page.
        </p>
      )}
    </div>
  );
}
